import { clsx } from 'clsx';
import styles from './EmptyWidget.module.scss';
import { Icon } from '../../../icon/Icon';
import { Button, ButtonProps } from '../../../button/Button';
import { DocLink } from '../../../docs/DocLink/DocLink';
import { useMemoizedDataIds } from '../../utils/dataId';
import { EMPTY_WIDGET_DATA_IDS_CONTRACT } from './data-ids';
import { useAnalyticsTracker } from '../../../tracking/hooks/useAnalyticsTracker';
import { LinkButton } from '../../../link/LinkButton';

export interface EmptyWidgetProps {
  dataId?: string;
  className?: string;
  title: string;
  subTitle?: string;
  docLink?: string;
  icon?: string;
  iconClass?: string;
  iconContainerBgClass?: string;
  primaryCTAProps?: Partial<ButtonProps>;
  secondaryCTAProps?: Partial<ButtonProps>;
  trackingProps?: object;
  compact?: boolean;
}

export function EmptyWidget({
  dataId = '',
  title,
  subTitle,
  className,
  icon,
  iconClass,
  iconContainerBgClass,
  docLink,
  primaryCTAProps,
  secondaryCTAProps,
  trackingProps = {},
  compact = false
}: EmptyWidgetProps) {
  const { eventTrack } = useAnalyticsTracker();

  const dataIds = useMemoizedDataIds(EMPTY_WIDGET_DATA_IDS_CONTRACT, dataId);

  return (
    <div
      className={clsx(styles.emptyWidgetContainer, className, compact ? styles.compactSpace : '')}
      data-id='empty-widget'
    >
      <div className={clsx(styles.iconContainer, iconContainerBgClass, compact ? 'mb-0' : '')}>
        <Icon
          name={icon || 'settings'}
          size={3}
          className={clsx(styles.icon, iconClass)}
          data-id={dataIds.icon}
        />
      </div>

      <div
        className={clsx('flex flex-col', compact ? '' : 'items-center')}
        style={compact ? { width: '90%' } : {}}
      >
        <span className={clsx(styles.title, compact ? '' : 'text-center')} data-id={dataIds.title}>
          {title}
        </span>

        {subTitle ? (
          <span
            className={clsx(compact ? '' : 'text-center', 'text-secondary')}
            data-id={dataIds.subTitle}
          >
            {subTitle}
          </span>
        ) : null}

        {compact && secondaryCTAProps?.onClick ? (
          <LinkButton
            className='mt-1'
            onClick={secondaryCTAProps.onClick}
            dataId={dataIds.secondaryCTA}
          >
            Try Again
          </LinkButton>
        ) : null}
      </div>

      {docLink ? (
        <DocLink
          dataId={dataIds.docLink}
          section='empty-widget'
          docLink={docLink}
          className={styles.docLink}
        />
      ) : null}

      {!compact && (!!primaryCTAProps?.children || !!secondaryCTAProps?.children) ? (
        <div className={styles.actionsContainer}>
          {secondaryCTAProps?.children ? (
            <Button
              {...secondaryCTAProps}
              className={clsx(primaryCTAProps?.children && 'mr-3')}
              size='sm'
              variation='outline'
              dataId={dataIds.secondaryCTA}
              onClick={() => {
                if (secondaryCTAProps.onClick) {
                  eventTrack({
                    action: '[Empty Widget] secondary cta click',
                    properties: {
                      title,
                      subTitle,
                      ...trackingProps
                    }
                  });

                  secondaryCTAProps.onClick();
                }
              }}
            >
              {secondaryCTAProps.children}
            </Button>
          ) : null}

          {primaryCTAProps?.children ? (
            <Button
              {...primaryCTAProps}
              dataId={dataIds.primaryCTA}
              size='sm'
              onClick={() => {
                if (primaryCTAProps.onClick) {
                  eventTrack({
                    action: '[Empty Widget] primary cta click',
                    properties: {
                      title,
                      subTitle,
                      ...trackingProps
                    }
                  });

                  primaryCTAProps.onClick();
                }
              }}
            >
              {primaryCTAProps.children}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
